export { KpiTile } from './KpiTile';
export type { KpiTileProps } from './KpiTile';

export { StatusPill, DORA_LEVEL_TONE } from './StatusPill';
export type { StatusPillProps, PillTone } from './StatusPill';

export { useHeaderRightSlot } from './HeaderSlot';

export { SpanTree } from './SpanTree';
export type { Span, SpanTreeProps } from './SpanTree';

/**
 * Utilitaire de test, exposé volontairement : il monte un en-tête factice avec
 * les classes que Material-UI produit en dev ET celles qu'il produit en
 * production, seule façon de tester `useHeaderRightSlot` contre la vraie
 * fragilité du sélecteur. Il suit le hook qu'il sert plutôt que de rester
 * dans `packages/app`, qui n'en est qu'un consommateur parmi d'autres.
 */
export {
  mountTestHeader,
  cleanupTestHeaders,
  DEV_HEADER_CLASSES,
  PROD_HEADER_CLASSES,
} from './mountTestHeader';

/**
 * Environnements de la plateforme (`platform.environments`). Le socle en est
 * le bon domicile : quatorze fichiers le consommaient depuis `packages/app`,
 * et chaque plugin extrait en a besoin — c'est ce que la spec appelle le
 * socle partagé, au même titre que `@backstage/plugin-catalog-react` pour les
 * plugins du marketplace.
 */
export {
  EnvironmentProvider,
  useEnvironment,
  useEnvironmentOrAll,
  ALL_ENVIRONMENTS,
  GlobalEnvSwitcher,
  readStoredEnv,
  storeEnv,
} from './EnvironmentContext';
export type { EnvConfig } from './EnvironmentContext';

/**
 * Police à chasse fixe de l'interface. Elle vivait dans
 * `components/catalog/database/shared.ts`, un fourre-tout d'où sept fichiers du
 * scaffolder ne prenaient QUE cette constante — un couplage du scaffolder au
 * domaine « base de données » qui n'avait aucune raison d'être.
 */
export const MONO = '"IBM Plex Mono", monospace';

export {
  useDbColors,
  useResourceEnvironment,
  useEntityRef,
  toCsv,
  downloadBlob,
} from './resourcePanel';
export type {
  DatabaseEntry,
} from './resourcePanel';

export { useOptionalEntity } from './useOptionalEntity';

/**
 * Les points d'attache des pages du noyau pour les widgets des modules
 * (tableau de bord, « Mon travail »). Voir `blueprints.ts`.
 */
export {
  DashboardWidgetBlueprint,
  dashboardWidgetAreaDataRef,
  MyWorkCardBlueprint,
  myWorkCardWidthDataRef,
} from './blueprints';
export type { DashboardWidgetArea, MyWorkCardWidth } from './blueprints';
export { SectionTitle } from './SectionTitle';

/**
 * État vide : prévu dans le socle par la spec, il
 * vivait dans `packages/app/src/components/theme/`, d'où un widget du
 * module `dora` ne pouvait pas les importer une fois extrait.
 */
export { EmptyState } from './EmptyState';
export type { EmptyStateProps } from './EmptyState';

/** Point d'attache des sections de la page « Mon profil ». Voir `profileSections.ts`. */
export { ProfileSectionBlueprint } from './profileSections';

/** API des toasts : les modules publient, le socle affiche. Voir `toasts.ts`. */
export { platformToastApiRef } from './toasts';
export type { PlatformToast, PlatformToastApi } from './toasts';

/** Crochets de déconnexion : les modules enregistrent, le socle exécute. Voir `signOutHooks.ts`. */
export { signOutHooksApiRef } from './signOutHooks';
export type { SignOutHooksApi } from './signOutHooks';

/** Onglets d'une fiche masqués par composant (`platform.io/hidden-tabs`). */
export { HIDDEN_TABS_ANNOTATION, isTabHidden, tabVisible } from './hiddenTabs';
export type { HideableTab } from './hiddenTabs';

/**
 * Jetons du thème Manewco (clair / sombre) et lecture du jeu actif. Voir `manewcoTokens.ts`.
 */
export {
  MANEWCO_LIGHT,
  MANEWCO_DARK,
  manewcoTokens,
  useManewcoTokens,
  envTextColor,
  DEFAULT_ENV_COLOR,
} from './manewcoTokens';
export type { ManewcoTokens } from './manewcoTokens';
